import {
  ExpressCheckoutElement,
  ExpressCheckoutElementProps,
  useElements,
} from "@stripe/react-stripe-js";
import { Box, Text, LinkText, VStack, Stack, PrimaryButton } from "flicket-ui";
import React, { FormEvent, useEffect, useState } from "react";
import Modal from "~components/common/Modal";
import { checkoutDebug } from "~lib/debug";
import Skeleton from "react-loading-skeleton";
import { useRouter } from "next/router";
import { removeQueryParams } from "~lib/helpers/queryParams";
import AnimatedLoadingSpinner from "~features/checkout/common/components/AnimatedLoadingSpinner/AnimatedLoadingSpinner";
import {
  AvailablePaymentMethods,
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";
import { useExpressCheckoutContext } from "./ExpressCheckout.context";
import { useSDK } from "~hooks";
import { OrderStatus, OrderQuery, VenueSeating } from "~graphql/sdk";
import { FormikProvider } from "formik";
import { DeliveryOptionsBlock } from "~features/checkout/common/components/DeliveryOptionsBlock";
import { TicketCoverFormPartial } from "~features/checkout/common/components/TicketCoverWithQueue";
import { useExpressCheckoutForm } from "../hooks/useExpressCheckoutForm";
import { Timer } from "~components/checkout/checkout.Timer";
import { Icon } from "~components";
import { Timer as PhosphorTimer } from "@phosphor-icons/react";
import { shouldShowTicketCover } from "~features/checkout/common/utils/ticketCover.util";
import { useModal } from "~components/common/Modal/useModal";
import { ExpressCheckoutOrderSummary } from "./OrderSummary/ExpressCheckoutOrderSummary";
import { showToast } from "~lib";
import { constructTermsAndConditionsUrlFromPath } from "~lib/helpers/constructTermsAndConditionsUrl";
import {
  SinglePageCheckoutProvider,
  useSinglePageCheckoutContext,
} from "~features/checkout/common/hooks/singlePageCheckoutContext";
import { ExpressCheckoutOrderSummaryProvider } from "./OrderSummary/ExpressCheckoutOrderSummaryProvider";
import { handleSentryError } from "../errors.utils";
import { MobileDivider } from "~features/checkout/common/components/MobileDivider";
import { OrderProvider } from "~features/checkout/common/hooks/OrderContext";
import { AnimateFadeIn } from "~components/AnimateReveal/AnimateFadeIn";
import { PrePurchaseQuestionsFormPartial } from "~features/checkout/common/components/PrePurchaseQuestionsFormPartial";
import { OptInToMarketingFormPartial } from "~features/checkout/common/components/OptInToMarketingFormPartial";
import { useCheckoutTrackingScope } from "~features/checkout/common/hooks/useCheckoutSentryScope";

export interface ExpressCheckoutModalContentProps {
  isLoading: boolean;
}

export function ExpressCheckoutModal() {
  const router = useRouter();
  const sdk = useSDK();

  return (
    <Modal
      alignModalTop={true}
      darkBackdrop={true}
      isOpen={router.query.modal === "express-checkout"}
      close={() => {
        if (router.query.orderId) {
          try {
            // Silently expire order
            void sdk.expireOrder({
              id: router.query.orderId as string,
            });
          } catch (error) {
            // Do nothing
          }
        }
        removeQueryParams(router, ["orderId", "paymentType", "modal"]);
      }}
      dialogContentProps={{
        width: "600px",
      }}
      showCloseButton={false}
    >
      {!router.query.orderId && <LoaderContent message="Securing tickets" />}
      {router.query.orderId && (
        <ExpressCheckoutModalContent isLoading={!router.query.orderId} />
      )}
    </Modal>
  );
}
export function ExpressCheckoutModalContent(
  props: ExpressCheckoutModalContentProps
) {
  return (
    <OrderProvider>
      <SinglePageCheckoutProvider>
        <ExpressCheckoutOrderSummaryProvider>
          <ExpressCheckoutModalOrderLoader {...props} />
        </ExpressCheckoutOrderSummaryProvider>
      </SinglePageCheckoutProvider>
    </OrderProvider>
  );
}

function ExpressCheckoutModalOrderLoader(
  props: ExpressCheckoutModalContentProps
) {
  const router = useRouter();
  const { order, error } = useSinglePageCheckoutContext();
  const { isLoadingExpressCheckoutElement } = useExpressCheckoutContext();
  const { close } = useModal();

  if (!order || !router.query.orderId || isLoadingExpressCheckoutElement) {
    return <LoaderContent />;
  }

  if (error) {
    return (
      <React.Fragment key="error">
        <Modal.Header>Unexpected error</Modal.Header>
        <Modal.Content>
          There was an unexpected error creating your order. Please try again.
        </Modal.Content>
        <Modal.Footer>
          <PrimaryButton onClick={close}>Close</PrimaryButton>
        </Modal.Footer>
      </React.Fragment>
    );
  }

  // Explicit workflow for expired orders
  if (order?.status === OrderStatus.Expired) {
    return (
      <React.Fragment key="expired">
        <Modal.Header>Order expired</Modal.Header>
        <Modal.Content>
          This order has expired. Please go back and try again.
        </Modal.Content>
        <Modal.Footer>
          <PrimaryButton onClick={close}>Close</PrimaryButton>
        </Modal.Footer>
      </React.Fragment>
    );
  }

  // For everything else that isn't pending, just show a generic error
  if (order?.status !== OrderStatus.Pending) {
    return (
      <React.Fragment key="pending">
        <Modal.Header>Invalid order</Modal.Header>
        <Modal.Content>Please go back and try again.</Modal.Content>
        <Modal.Footer>
          <PrimaryButton onClick={close}>Close</PrimaryButton>
        </Modal.Footer>
      </React.Fragment>
    );
  }

  return <ExpressCheckoutModalContentForm {...props} />;
}

type Query = {
  paymentType: keyof AvailablePaymentMethods;
  orderId: string;
  modal: "express-checkout";
};

function ExpressCheckoutModalContentForm(
  props: ExpressCheckoutModalContentProps
) {
  useCheckoutTrackingScope("express");

  const router = useRouter();
  const query = router.query as Query;

  const elements = useElements();
  const { formik, handleConfirm, paymentInProgress } = useExpressCheckoutForm();

  const { order, isQueueProcessing, totals } = useSinglePageCheckoutContext();

  const showTicketCover = shouldShowTicketCover(order);

  useEffect(() => {
    if ((totals?.remainingPayment >= 0, elements)) {
      elements.update({ amount: Math.round(totals.remainingPayment * 100) });
    }
  }, [totals, elements]);

  function handleElementLoadError(e) {
    console.error("Strip Express Checkout element failed to load", e);

    // Close the modal and display and error
    removeQueryParams(router, ["modal", "paymentType", "orderId"]);

    showToast("Unable to load payment methods. Please try again", "error");

    handleSentryError("Error loading stripe express checkout element", e);
  }

  const showHiddenSubmit =
    !formik.isValid ||
    (showTicketCover && formik.values?.ticketCover === undefined);

  return (
    <>
      {/* Payment progress is an overly because the Stripe element needs to be
    mounted while payment is being confirmed */}
      {paymentInProgress && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={10}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
          bg={"rgba(255, 255, 255, 0.95)" as any}
        >
          <LoaderContent message="Payment in progress..." />
        </Box>
      )}

      <FormikProvider value={formik}>
        <Modal.Header>
          Complete your order
          {!paymentInProgress && <ExpressTimer order={order} />}
        </Modal.Header>
        <Modal.Content>
          <AnimateFadeIn>
            <VStack gap={4}>
              <DeliveryOptionsBlock />

              <TicketCoverFormPartial />

              <PrePurchaseQuestionsFormPartial />

              <OptInToMarketingFormPartial />

              <MobileDivider />

              <VStack gap="6/4">
                <VStack gap={2}>
                  <ExpressCheckoutOrderSummary />

                  <Text variant="small">
                    By completing this purchase you agree to the{" "}
                    <LinkText
                      target="_blank"
                      href={constructTermsAndConditionsUrlFromPath(
                        router.asPath
                      )}
                    >
                      Event Terms and Conditions
                    </LinkText>{" "}
                    and{" "}
                    <LinkText target="_blank" href="/privacy-policy">
                      Privacy Policy
                    </LinkText>
                    .
                  </Text>

                  <ExpressCheckoutElementAndWrapper
                    hiddenSubmitClick={() => {
                      void formik.submitForm();
                    }}
                    paymentType={query.paymentType}
                    isOrderUpdating={isQueueProcessing}
                    showHiddenSubmit={showHiddenSubmit}
                    handleElementLoadError={handleElementLoadError}
                    handleConfirm={handleConfirm}
                  />
                </VStack>
              </VStack>
            </VStack>
          </AnimateFadeIn>
        </Modal.Content>
      </FormikProvider>
    </>
  );
}

function LoaderContent({ message = "" }: { message?: string }) {
  return (
    <Modal.Content>
      <Stack
        direction="vertical"
        gap={1}
        alignItems="center"
        justifyContent="center"
        height="386px"
        padding={1}
      >
        <Text variant="header.M" textAlign="center">
          {message}
        </Text>
        <AnimatedLoadingSpinner loading={true} onComplete={console.log} />
      </Stack>
    </Modal.Content>
  );
}

const TWO_MINUTES = 60 * 2;

function ExpressTimer({ order }: { order?: OrderQuery["order"] }) {
  return (
    <Timer
      order={order}
      isGeneralAdmissionEvent={
        order?.event?.venue?.seating === VenueSeating.NonSeated ||
        order?.membership?.venue?.seating === VenueSeating.NonSeated
      }
    >
      {({ secondsRemaining, seconds }) => {
        if (seconds > TWO_MINUTES) {
          return null;
        }

        const criticalTime = seconds <= 60;

        return (
          <Text
            variant="small"
            color={criticalTime ? "error" : undefined}
            fontWeight={criticalTime ? "bold" : undefined}
            width={"100%"}
            justifySelf={"flex-start"}
            mt={"1/4"}
            d="flex"
            alignItems={"center"}
          >
            <Icon
              icon={
                <PhosphorTimer
                  size={16}
                  weight={criticalTime ? "bold" : "regular"}
                />
              }
              mr={"1/2"}
              verticalAlign={"middle"}
            />
            Time left {secondsRemaining}
          </Text>
        );
      }}
    </Timer>
  );
}

interface ElementWrapperProps {
  paymentType: keyof AvailablePaymentMethods;
  isOrderUpdating: boolean;
  showHiddenSubmit: boolean;
  hiddenSubmitClick: () => void;
  handleElementLoadError: ExpressCheckoutElementProps["onLoadError"];
  handleConfirm: (e: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
}

function ExpressCheckoutElementAndWrapper({
  paymentType,
  showHiddenSubmit,
  hiddenSubmitClick,
  handleElementLoadError,
  handleConfirm,
  isOrderUpdating,
}: ElementWrapperProps) {
  const [elementLoading, setElementLoading] = useState(true);
  const showLoadingButton = elementLoading;

  console.log({ showHiddenSubmit });
  return (
    <>
      {/* Set box height to 48px which is the same as the express checkout button to prevent layout shifts */}
      <Box
        position={"relative"}
        height="48px"
        transition="opacity 0.16s 0.16s"
        opacity={isOrderUpdating ? 0.5 : 1}
        pointerEvents={isOrderUpdating ? "none" : "auto"}
        css={`
          .StripeElement {
            transition: none !important;
            margin: -4px !important; // Offsets p-HeightObserverProvider-container 4px margin
            width: 100% !important;

            & div,
            iframe {
              margin: 0 !important;
            }
          }
        `}
      >
        {showLoadingButton && (
          <Box
            position={"absolute"}
            right={0}
            left={0}
            top={0}
            bottom={0}
            zIndex={1000}
            // react-loading-skeleton is made to sit inside text, so we need to finesse some properties
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            lineHeight={"48px" as any}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            fontSize={"48px" as any}
          >
            <Skeleton />
          </Box>
        )}
        {showHiddenSubmit && (
          <Box
            position={"absolute"}
            right={0}
            left={0}
            height="48px"
            cursor="pointer"
            zIndex={1000}
            onClick={(e: FormEvent<HTMLDivElement>) => {
              e.stopPropagation();
              hiddenSubmitClick?.();
            }}
          />
        )}
        <ExpressCheckoutElement
          onReady={(e) => {
            checkoutDebug("Stripe checkout ready", e);
            setElementLoading(false);
          }}
          onConfirm={handleConfirm}
          onLoadError={handleElementLoadError}
          options={{
            billingAddressRequired: true,
            phoneNumberRequired: true,
            emailRequired: true,
            paymentMethods: {
              applePay: "never",
              googlePay: "never",
              link: "never",
              amazonPay: "never",
              klarna: "never",
              paypal: "never",

              // This will tell Stripe to only show the seleced payment method
              [paymentType]: "auto",
            },
            buttonHeight: 48,
          }}
        />
      </Box>
    </>
  );
}
